
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d621ipc7f73jei4pha0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-leather-dark text-cream border-t-4 border-orange-burnt">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 sm:gap-16">
          <div className="md:col-span-5">
            <img
              src="https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/60de7e36a1bed6e668220b00c0ac7762.png"
              alt="Anson Road Records"
              className="h-14 sm:h-20 w-auto mb-6 sm:mb-8"
            />
            <div className="w-20 h-1 bg-orange-burnt mb-6"></div>
            <p className="font-sans text-sm sm:text-base font-medium leading-relaxed mb-3 uppercase tracking-wide">
              {t('footer.tagline')}
            </p>
            <p className="font-sans text-xs sm:text-sm font-light leading-relaxed text-cream/80 mb-2">
              {t('footer.services')}
            </p>
            <p className="font-sans text-base sm:text-lg font-light leading-relaxed text-cream/80">
              {t('footer.slogan')}
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display text-xl sm:text-2xl font-black mb-4 sm:mb-6 text-orange-burnt uppercase">
              {t('footer.navigate')}
            </h4>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <Link
                  to="/"
                  className="font-sans text-sm uppercase tracking-wider hover:text-orange-burnt transition-colors cursor-pointer font-medium"
                >
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/records"
                  className="font-sans text-sm uppercase tracking-wider hover:text-orange-burnt transition-colors cursor-pointer font-medium"
                >
                  {t('nav.records')}
                </Link>
              </li>
              <li>
                <Link
                  to="/licensing-publishing"
                  className="font-sans text-sm uppercase tracking-wider hover:text-orange-burnt transition-colors cursor-pointer font-medium"
                >
                  {t('nav.licensingPublishing')}
                </Link>
              </li>
              <li>
                <Link
                  to="/management"
                  className="font-sans text-sm uppercase tracking-wider hover:text-orange-burnt transition-colors cursor-pointer font-medium"
                >
                  {t('nav.management')}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="font-sans text-sm uppercase tracking-wider hover:text-orange-burnt transition-colors cursor-pointer font-medium"
                >
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-display text-xl sm:text-2xl font-black mb-4 sm:mb-6 text-orange-burnt uppercase">
              {t('footer.stayConnected')}
            </h4>
            <p className="font-sans text-sm mb-4 sm:mb-6 text-cream/80 leading-relaxed">
              {t('footer.newsletterText')}
            </p>
            <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form">
              <div className="flex flex-col gap-3 sm:gap-4">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.emailPlaceholder')}
                  required
                  maxLength={100}
                  className="bg-transparent border-2 border-cream/30 py-3 sm:py-4 px-4 text-cream placeholder-cream/50 focus:outline-none focus:border-orange-burnt transition-colors font-sans text-sm uppercase tracking-wider"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-orange-burnt text-cream px-8 py-3 sm:py-4 rounded-none font-sans text-sm font-bold uppercase tracking-wider hover:bg-cream hover:text-leather-dark transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 border-2 border-orange-burnt"
                >
                  {isSubmitting ? t('footer.subscribing') : t('footer.subscribe')}
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-orange-soft text-sm mt-4 uppercase tracking-wide">
                  {t('footer.thankYou')}
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm mt-4 uppercase tracking-wide">
                  {t('footer.error')}
                </p>
              )}
            </form>

            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-cream/20">
              <a
                href="mailto:info@santosranchrecords.com"
                className="font-sans text-xs sm:text-sm hover:text-orange-burnt transition-colors cursor-pointer uppercase tracking-wider font-medium break-all"
              >
                info@santosranchrecords.com
              </a>

              <div className="flex items-center gap-4 sm:gap-5 mt-5 sm:mt-6">
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border-2 border-cream/30 hover:border-orange-burnt hover:text-orange-burnt transition-all cursor-pointer"
                  aria-label="Follow us on Instagram"
                >
                  <i className="ri-instagram-line text-base sm:text-lg"></i>
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border-2 border-cream/30 hover:border-orange-burnt hover:text-orange-burnt transition-all cursor-pointer"
                  aria-label="Follow us on Facebook"
                >
                  <i className="ri-facebook-fill text-base sm:text-lg"></i>
                </a>
                <a
                  href="https://spotify.com"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border-2 border-cream/30 hover:border-orange-burnt hover:text-orange-burnt transition-all cursor-pointer"
                  aria-label="Listen on Spotify"
                >
                  <i className="ri-spotify-fill text-base sm:text-lg"></i>
                </a>
                <a
                  href="https://music.apple.com"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border-2 border-cream/30 hover:border-orange-burnt hover:text-orange-burnt transition-all cursor-pointer"
                  aria-label="Listen on Apple Music"
                >
                  <i className="ri-apple-fill text-base sm:text-lg"></i>
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border-2 border-cream/30 hover:border-orange-burnt hover:text-orange-burnt transition-all cursor-pointer"
                  aria-label="Watch on YouTube"
                >
                  <i className="ri-youtube-fill text-base sm:text-lg"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t-2 border-orange-burnt/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-sans text-xs text-cream/60 uppercase tracking-wider text-center">
              &copy; 2026 Anson Road Records, subsidiary of ranchfire multimedia, llc. All Rights Reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-xs text-cream/60 hover:text-orange-burnt transition-colors cursor-pointer uppercase tracking-wider"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
